import {
  QueryObserverResult,
  QueryStatus,
  useQuery,
} from '@tanstack/react-query';
import {
  ApiError,
  ApiRequestOptions,
  GeneratedZorroPy,
  isSuccess,
} from '@zorro/clients';
import { ExtractClassMethods } from '@zorro/types';
import axios, { HttpStatusCode } from 'axios';

export type ZorroPyMethods = ExtractClassMethods<
  typeof GeneratedZorroPy.DefaultService
> &
  ExtractClassMethods<typeof GeneratedZorroPy.AgentShoppingConsultantService>;
export type ZorroPyMethodNames = keyof ZorroPyMethods;
type Falsy = false | 0 | -0 | '' | null | undefined;

type UseZorroPyQueryProps<T extends ZorroPyMethodNames> = {
  method: T;
  params?: Parameters<ZorroPyMethods[T]> | Falsy;
  enabled?: boolean;
  staleTime?: number;
  /** Subject employee for a "view as employee" session; also in the query key. */
  impersonatedEmployeeId?: string;
};

type UseZorroPyQueryReturnType<T extends ZorroPyMethodNames> = {
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  refetch: () => Promise<
    QueryObserverResult<Awaited<ReturnType<ZorroPyMethods[T]>> | undefined>
  >;
  data?: Awaited<ReturnType<ZorroPyMethods[T]>>;
  error?: unknown;
  status: QueryStatus;
};

const ZORRO_PY_API_ROUTE = '/api/zorro-py-client';

export function useZorroPyQuery<T extends ZorroPyMethodNames>({
  method,
  params,
  enabled = true,
  staleTime = undefined,
  impersonatedEmployeeId,
}: UseZorroPyQueryProps<T>): UseZorroPyQueryReturnType<T> {
  const { isLoading, isFetching, isError, refetch, data, error, status } =
    useQuery({
      queryKey: [method, impersonatedEmployeeId, ...(params || [])],
      queryFn: async () => {
        if (!params) {
          return null;
        }

        try {
          const { data } = await axios.post(ZORRO_PY_API_ROUTE, {
            method,
            params,
            impersonatedEmployeeId,
          });

          return data;
        } catch (error) {
          let message = error.message || 'unknown error';

          if (error.response) {
            const { status, data } = error.response;

            if (status === HttpStatusCode.Unauthorized) {
              window.location.reload();
              // eslint-disable-next-line consistent-return
              return;
            }

            throw new ApiError(
              {} as unknown as ApiRequestOptions,
              {
                url: ZORRO_PY_API_ROUTE,
                ok: isSuccess(data.status),
                status,
                statusText: data.statusText,
                body: data.body,
              },
              message
            );
          }

          if (error.request) {
            message = `the request was made but no response was received. ${message}`;
          }

          throw new ApiError(
            {} as unknown as ApiRequestOptions,
            {
              url: ZORRO_PY_API_ROUTE,
              ok: false,
              status: 418,
              statusText: "I'm a teapot",
              body: {},
            },
            message
          );
        }
      },
      enabled,
      ...(staleTime !== undefined ? { staleTime } : {}), // omit to inherit defaults
    });

  return {
    isLoading,
    isFetching,
    isError,
    refetch,
    data: data ?? undefined,
    error,
    status,
  } as UseZorroPyQueryReturnType<T>;
}
